// The headline figures for a place or a project, in one component so every card tells the same story (2026-09-18).
//
// Before this, four cards each computed `min(expected, max(0, on_site - awaiting_receipt))` and then showed
// "4 / 8 on site" next to "2 arrived, not received" and a stock-count chip — three different denominators in one
// row, and nothing accounted for the other four assets. Now the bar and the chips below it partition `expected`
// exactly (on site + in transit + elsewhere + missing + not seen), and the arrivals chip is set apart because
// those assets are not in `expected` at all until someone receives them.
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type GapKind, gaps, type PlaceTally as Tally } from '@fleetspoke/core/sites';
import { useTranslation } from 'react-i18next';

/** Bar segments, in the order they stack. `on_site` first: the part that is where it should be. */
const SEGMENT_COLOR: Record<'on_site' | GapKind, string> = {
  on_site: 'success.main',
  missing: 'error.main',
  in_transit: 'info.main',
  off_site: 'warning.light',
  not_seen: 'action.disabledBackground',
};

const GAP_LABEL: Record<GapKind, string> = {
  missing: 'sites.missing',
  in_transit: 'sites.in_transit',
  off_site: 'sites.off_site',
  not_seen: 'sites.not_seen',
};

/** A stacked bar whose segments are the custody buckets — the identity `sum = expected` made visible. */
export function TallyBar({ tally, sx }: { tally: Tally; sx?: SxProps<Theme> }) {
  const { t } = useTranslation();
  const base = { height: 6, borderRadius: 3, ...(sx as object) } as SxProps<Theme>;
  if (!tally.expected) {
    return <Box sx={{ ...(base as object), bgcolor: 'action.hover' }} />;
  }
  const segments: { kind: 'on_site' | GapKind; n: number }[] = [
    { kind: 'on_site', n: tally.on_site },
    ...gaps(tally),
  ];
  return (
    <Stack
      direction="row"
      sx={{ ...(base as object), overflow: 'hidden' }}
      role="img"
      aria-label={segments
        .map((s) => `${s.n} ${t(s.kind === 'on_site' ? 'sites.on_site' : GAP_LABEL[s.kind])}`)
        .join(' · ')}
    >
      {segments.map((s) => (
        <Box
          key={s.kind}
          sx={{ width: `${(s.n / tally.expected) * 100}%`, bgcolor: SEGMENT_COLOR[s.kind] }}
        />
      ))}
    </Stack>
  );
}

/**
 * A chip per non-empty gap bucket, so the assets that are not on site are all accounted for, then the arrivals
 * chip after a divider — those are outside the total. `extra` is appended (the cards put the stock-count chip there).
 */
export function TallyChips({
  tally,
  extra,
  sx,
}: {
  tally: Tally;
  extra?: React.ReactNode;
  sx?: SxProps<Theme>;
}) {
  const { t } = useTranslation();
  const gap = gaps(tally);
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={sx}>
      {gap.map((g) => (
        <Chip
          key={g.kind}
          size="small"
          variant="outlined"
          color={g.kind === 'missing' ? 'error' : g.kind === 'in_transit' ? 'info' : 'default'}
          icon={
            g.kind === 'missing' ? (
              <ErrorOutlineIcon />
            ) : g.kind === 'in_transit' ? (
              <LocalShippingOutlinedIcon />
            ) : undefined
          }
          label={`${g.n} ${t(GAP_LABEL[g.kind]).toLowerCase()}`}
        />
      ))}
      {/* inbound, deliberately after a divider: not part of the total above until received */}
      {tally.awaiting_receipt > 0 ? (
        <>
          {gap.length ? (
            <Box sx={{ width: '1px', height: 18, bgcolor: 'divider' }} aria-hidden />
          ) : null}
          <Tooltip title={t('sites.to_receive_help')}>
            <Chip
              size="small"
              color="warning"
              variant="filled"
              icon={<LocalShippingOutlinedIcon />}
              label={t('sites.to_receive', { n: tally.awaiting_receipt })}
            />
          </Tooltip>
        </>
      ) : null}
      {extra}
    </Stack>
  );
}

/** "4 / 8 on site", the stacked bar, then the chips that account for the rest. The card layout. */
export function PlaceTallyFigures({ tally, extra }: { tally: Tally; extra?: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <>
      <Tooltip title={t('sites.tally_help', { n: tally.on_site, total: tally.expected })}>
        <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ width: 'fit-content' }}>
          <Typography
            variant="h4"
            component="span"
            sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}
          >
            {tally.on_site}
          </Typography>
          <Typography
            variant="h6"
            component="span"
            color="text.secondary"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            / {tally.expected}
          </Typography>
          <Typography variant="body2" component="span" color="text.secondary">
            {t('sites.on_site').toLowerCase()}
          </Typography>
        </Stack>
      </Tooltip>
      <TallyBar tally={tally} sx={{ mt: 1.25, mb: 1.5 }} />
      <TallyChips tally={tally} extra={extra} />
    </>
  );
}
